import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import AuthenticatedPage from '../../../../containers/AuthenticatedPage'
import Table, { Row, Cell, Header } from '../../../../components/Table'
import exportFromJSON from 'export-from-json'
import API from '../../../../lib/api'
import { APG_GATEWAY_HOST } from '../../../../config/app'
import './style.css'

class PicklistDetail extends Component {
    constructor(props) {
        super(props)
        const picklistId = this.props.match.params.picklistId

        this.state = {
            picklistId,
            items: [],
            filteredItems: [],
            headerData: null,
            searchQuery: '',
            loading: true,
            error: null,
        }
    }

    componentDidMount() {
        this.fetchPicklistDetail()
    }

    fetchPicklistDetail = () => {
        this.setState({ loading: true, error: null })
        const { picklistId } = this.state

        const api = new API({
            url: `${APG_GATEWAY_HOST}/seller-order-service/picklists/admin/${picklistId}`
        })

        api.get({})
            .then(response => {
                const body = response || {}
                const actualData = body.data || body || {}

                // Extract and flatten items from parcels
                let items = []
                if (actualData.parcels && Array.isArray(actualData.parcels)) {
                    actualData.parcels.forEach(parcel => {
                        if (parcel.items && Array.isArray(parcel.items)) {
                            const parsedItems = parcel.items.map(item => ({
                                id: item.sku,
                                articleNo: item.sku,
                                articleDesc: item.productName,
                                barcode: (item.barcodes || [])[0] || '',
                                spc: item.spc,
                                orderedQty: item.orderedQuantity,
                                pickedQty: item.pickedQuantity,
                                remainingQty: Math.max(0, (item.orderedQuantity || 0) - (item.pickedQuantity || 0))
                            }))
                            items = items.concat(parsedItems)
                        }
                    })
                } else if (actualData.items && Array.isArray(actualData.items)) {
                    // Fallback if items are directly at root
                    items = actualData.items.map(item => ({
                        id: item.sku,
                        articleNo: item.sku,
                        articleDesc: item.productName,
                        barcode: (item.barcodes || [])[0] || '',
                        spc: item.spc,
                        orderedQty: item.orderedQuantity,
                        pickedQty: item.pickedQuantity,
                        remainingQty: Math.max(0, (item.orderedQuantity || 0) - (item.pickedQuantity || 0))
                    }))
                }

                this.setState({
                    headerData: actualData,
                    items: items,
                    filteredItems: items,
                    loading: false
                })
            })
            .catch(error => {
                console.error('Error fetching picklist detail:', error)
                this.setState({
                    loading: false,
                    error: error.message || 'Failed to fetch picklist detail'
                })
            })
    }

    handleSearch = (e) => {
        const searchQuery = e.target.value.toLowerCase()
        const filteredItems = this.state.items.filter(item =>
            (item.articleNo || '').toLowerCase().includes(searchQuery) ||
            (item.articleDesc || '').toLowerCase().includes(searchQuery) ||
            (item.barcode || '').toLowerCase().includes(searchQuery)
        )
        this.setState({ searchQuery: e.target.value, filteredItems })
    }

    calculateTotals = () => {
        const { filteredItems } = this.state
        return filteredItems.reduce((acc, item) => ({
            orderedQty: acc.orderedQty + item.orderedQty,
            pickedQty: acc.pickedQty + item.pickedQty,
            remainingQty: acc.remainingQty + item.remainingQty,
        }), { orderedQty: 0, pickedQty: 0, remainingQty: 0 })
    }

    handleBack = () => {
        this.props.history.push('/marketplace/picker-admin')
    }

    handleDownload = () => {
        const { picklistId, filteredItems } = this.state
        const totals = this.calculateTotals()

        // Prepare data for Excel export
        const exportData = [
            ...filteredItems.map(item => ({
                'Article No.': item.articleNo,
                'Article Description': item.articleDesc,
                'Barcode': item.barcode,
                'SPC': item.spc,
                'Ordered Qty': item.orderedQty,
                'Picked Qty': item.pickedQty,
                'Remaining Qty': item.remainingQty,
            })),
            // Add totals row
            {
                'Article No.': '',
                'Article Description': '',
                'Barcode': '',
                'SPC': 'TOTAL',
                'Ordered Qty': totals.orderedQty,
                'Picked Qty': totals.pickedQty,
                'Remaining Qty': totals.remainingQty,
            }
        ]

        exportFromJSON({
            data: exportData,
            fileName: `Picklist_${picklistId}_${new Date().toISOString().split('T')[0]}`,
            exportType: exportFromJSON.types.xls,
        })
    }

    render() {
        const { menu } = this.props
        const { picklistId, filteredItems, loading, error, headerData } = this.state
        const totals = this.calculateTotals()

        return (
            <AuthenticatedPage menu={menu} className="picklist-detail-page">
                <div className="page-header">
                    <div className="header-left">
                        <button className="back-button" onClick={this.handleBack}>
                            ←
                        </button>
                        <h1 className="page-title">Picklist: {picklistId}</h1>
                    </div>
                    <div className="header-right-actions">
                        <div className="search-container">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search"
                                value={this.state.searchQuery}
                                onChange={this.handleSearch}
                            />
                            <span className="search-icon">🔍</span>
                        </div>
                        <button
                            className="primary button download-button"
                            onClick={this.handleDownload}
                            disabled={loading || !!error || filteredItems.length === 0}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Download
                        </button>
                    </div>
                </div>

                <div className="table-container">
                    {loading ? (
                        <div className="loading-state">Loading picklist details...</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : filteredItems.length > 0 ? (
                        <>
                            <Table>
                                <Header>
                                    <Cell>Article No.</Cell>
                                    <Cell>Article Desc.</Cell>
                                    <Cell>Barcode</Cell>
                                    <Cell>SPC</Cell>
                                    <Cell>Ordered Qty</Cell>
                                    <Cell>Picked Qty</Cell>
                                    <Cell>Remaining Qty</Cell>
                                </Header>
                                {filteredItems.map((item) => (
                                    <Row key={item.id}>
                                        <Cell>{item.articleNo}</Cell>
                                        <Cell>{item.articleDesc}</Cell>
                                        <Cell>{item.barcode}</Cell>
                                        <Cell>{item.spc}</Cell>
                                        <Cell className="qty-cell">{item.orderedQty}</Cell>
                                        <Cell className="qty-cell">{item.pickedQty}</Cell>
                                        <Cell className="qty-cell">{item.remainingQty}</Cell>
                                    </Row>
                                ))}
                                {/* Totals Row */}
                                <Row className="totals-row">
                                    <Cell></Cell>
                                    <Cell></Cell>
                                    <Cell></Cell>
                                    <Cell></Cell>
                                    <Cell className="qty-cell total-value">{totals.orderedQty}</Cell>
                                    <Cell className="qty-cell total-value">{totals.pickedQty}</Cell>
                                    <Cell className="qty-cell total-value">{totals.remainingQty}</Cell>
                                </Row>
                            </Table>
                        </>
                    ) : (
                        <div className="empty-state">
                            {headerData?.parcels === null ? 'No parcels available for this picklist' : 'No items found'}
                        </div>
                    )}
                </div>
            </AuthenticatedPage>
        )
    }
}

export default withRouter(PicklistDetail)
